<template>
    <div id="muven-channel-credentials" class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Credenciais</p>
        </div>

        <div class="muven-content">
            <Breadcrumb :items="breadcrumbList" class="mb-3" v-if="!loading"/>

            <Loading page v-if="loading" />

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="canal"
                :identifier="$route.params.id"
                list-path="/canais"
                list-label="Voltar para canais"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <div class="channel-credentials" v-if="!loading && !loadError">
                <ChannelPageHeaderCmp :channel="channel" eyebrow="Credenciais">
                    <span class="cc-status" :class="`is-${credentialsState}`">
                        <span class="cc-status-dot"></span>
                        {{credentialsStateLabel}}
                    </span>
                </ChannelPageHeaderCmp>

                <div class="shopify-rehomolog-alert" v-if="channel.isShopify()">
                    <div class="shopify-rehomolog-alert__stripe"></div>
                    <div class="shopify-rehomolog-alert__icon-col">
                        <div class="shopify-rehomolog-alert__icon-ring">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 11c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1s1 .45 1 1v4c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" fill="currentColor"/>
                            </svg>
                        </div>
                    </div>
                    <div class="shopify-rehomolog-alert__body">
                        <div class="shopify-rehomolog-alert__badge">
                            <span class="shopify-rehomolog-alert__badge-dot"></span>
                            Mudanças na Shopify
                        </div>
                        <p class="shopify-rehomolog-alert__title">Nova política de autenticação da Shopify</p>
                        <p class="shopify-rehomolog-alert__desc">
                            Prezado cliente, a Shopify atualizou suas políticas de autenticação para integrações, o que levou o Muven a entrar em processo de homologação para o novo padrão. 
                            Por esse motivo, <strong>novas autenticações estão temporariamente indisponíveis</strong>, porque a Shopify não disponibiliza mais as credenciais abaixo.                        
                        </p>
                        <p class="shopify-rehomolog-alert__sub">
                            O Muven já atende às novas exigências e o processo está na etapa final para aprovação. No entanto, a conclusão do prazo depende exclusivamente da Shopify.
                        </p>
                    </div>
                </div>

                <ChannelCustomCredentialsCmp 
                    ref="form"
                    :channel="channel"
                    :schema="schema"
                    :credentials="credentials"
                    :link-tutorial-credentials="schema.linkTutorialCredentials"
                    v-if="channel.isCustom()"
                    @state="credentialsState = $event" />

                <ChannelCredentialsSimpleCmp 
                    ref="form"
                    :channel="channel"
                    :credentials-form="credentialsForm"
                    :link-tutorial-credentials="schema.linkTutorialCredentials"
                    v-if="!channel.credentialsByStore && !channel.isCustom()"
                    @state="credentialsState = $event" />

                <ChannelCredentialsByStoreCmp 
                    ref="form"
                    :channel="channel"
                    :link-tutorial-credentials="schema.linkTutorialCredentials"
                    v-if="channel.credentialsByStore && !channel.isCustom()"
                    @state="credentialsState = $event" />
            </div>

            <!-- Fora de .channel-credentials: a animação de entrada cria um contexto
                 de empilhamento e o modal ficaria embaixo do backdrop do Bootstrap. -->
            <UnsavedChangesModalCmp ref="unsavedChanges" :modal-id="ID_UNSAVED_CHANGES_MODAL" />
        </div>
    </div>
</template>

<script>
import { ChannelModel } from '@/channel/models/ChannelModel'
import channelService from '@/channel/services/ChannelService'
import ChannelCustomCredentialsCmp from '@/channel/components/ChannelCustomCredentialsCmp'
import ChannelCredentialsSimpleCmp from '@/channel/components/ChannelCredentialsSimpleCmp'
import ChannelCredentialsByStoreCmp from '@/channel/components/ChannelCredentialsByStoreCmp'
import dynamicCredentialsDataParser from '../utils/DynamicCredentialsDataParser'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { getChannelNotFoundBreadcrumb } from '../utils/ChannelNotFoundBreadcrumb'
import ChannelPageHeaderCmp from '@/channel/components/ChannelPageHeaderCmp.vue'
import UnsavedChangesModalCmp from '@/utils/components/UnsavedChangesModalCmp.vue'

const ID_UNSAVED_CHANGES_MODAL = 'muven-credentials-unsaved-changes-modal'

const CREDENTIALS_STATE_LABELS = {
    unconfigured: 'Não configurado',
    configured: 'Configurado',
    testing: 'Testando conexão',
    ok: 'Conexão OK',
    failed: 'Falha no teste',
}

export default {
    name: 'ChannelCredentialsView',
    data() {
        return {
            loading: true,            
            channel: new ChannelModel(),
            breadcrumbList: [],
            credentials: {},
            credentialsForm: {},
            schema: {},
            // Vem do formulário filho (evento `state`)
            credentialsState: 'unconfigured',
            ID_UNSAVED_CHANGES_MODAL,
        }
    },
    // O formulário "Por loja" também pergunta antes de trocar de loja com alteração pendente
    provide() {
        return {
            confirmDiscardChanges: () => this.$refs.unsavedChanges.ask(),
        }
    },
    components: {
        ChannelCustomCredentialsCmp,
        ChannelCredentialsSimpleCmp,
        ChannelCredentialsByStoreCmp,
        ChannelPageHeaderCmp,
        UnsavedChangesModalCmp,
    },
    mixins: [
        ResourceLoadError,
    ],
    mounted() {
        this.load()
        window.addEventListener('beforeunload', this.onBeforeUnload)
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload)
    },
    async beforeRouteLeave(to, from, next) {
        if (!this.hasUnsavedChanges()) {
            return next()
        }

        next(await this.$refs.unsavedChanges.ask())
    },
    computed: {
        credentialsStateLabel() {
            return CREDENTIALS_STATE_LABELS[this.credentialsState] || CREDENTIALS_STATE_LABELS.unconfigured
        },
    },
    methods: {
        hasUnsavedChanges() {
            const form = this.$refs.form

            // Durante o salvar o próprio formulário navega de volta para as configurações
            return !!form && form.isDirty && !form.savingContent
        },
        onBeforeUnload(event) {
            if (!this.hasUnsavedChanges()) {
                return
            }

            event.preventDefault()
            event.returnValue = ''
        },
        async load() {
            this.loading = true
            this.loadError = null

            const channelId = this.$route.params.id

            try {
                this.schema = await channelService.getCredentialsSchema(channelId)
                this.channel = await channelService.getChannelById(channelId)
                this.credentials = await channelService.getCredentials(channelId)
                this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, this.credentials)

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Canais',
                        path: '/canais'
                    },
                    {
                        id: 1,
                        description: `Configurações ${this.channel.name}`,
                        channel: this.channel,
                        path: `/canais/${channelId}/configuracoes`,
                    },
                    {
                        id: 2,
                        description: `Credenciais`,
                        path: '',
                    },
                ]
            } catch(e) {
                this.loadError = e
                this.breadcrumbList = getChannelNotFoundBreadcrumb(this.$route.params.id)
            }

            this.loading = false
        },
    },
}
</script>

<style scoped>
.channel-credentials {
    max-width: 860px;
    margin: 0 auto;
}

/* ── Selo de estado (no cabeçalho), mesmo da autenticação ── */

.cc-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 14px 5px 12px;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface-2);
    color: var(--text-muted);
    font-weight: 700;
}

.cc-status.is-configured,
.cc-status.is-ok {
    border-color: var(--success-border);
    background: var(--success-bg);
    color: var(--success-fg);
}

.cc-status.is-failed {
    border-color: var(--danger-border);
    background: var(--danger-bg);
    color: var(--danger-fg);
}

.cc-status.is-testing {
    border-color: var(--tint-brand-border);
    background: var(--tint-brand-bg);
    color: var(--brand);
}

.cc-status.is-testing .cc-status-dot {
    animation: cc-pulse 1.2s ease-in-out infinite;
}

.cc-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

@keyframes cc-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: .3; }
}

@media (prefers-reduced-motion: reduce) {
    .cc-status-dot {
        animation: none !important;
    }
}

.shopify-rehomolog-alert {
    margin-top: 16px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    position: relative;
    background: var(--warning-bg);
    border: 1px solid var(--warning-border);
    border-radius: 10px;
    padding: 20px 22px;
    overflow: hidden;
    box-shadow: 0 2px 8px color-mix(in srgb, var(--warning-fg) 8%, transparent);
    animation: alertFadeIn 0.4s ease;
}

@keyframes alertFadeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
}

.shopify-rehomolog-alert__stripe {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--warning-solid), var(--warning-fg));
    border-radius: 10px 0 0 10px;
}

.shopify-rehomolog-alert__icon-col {
    flex-shrink: 0;
    padding-top: 2px;
}

.shopify-rehomolog-alert__icon-ring {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--warning-bg);
    border: 1.5px solid var(--warning-border);
    color: var(--warning-fg);
    display: flex;
    align-items: center;
    justify-content: center;
}

.shopify-rehomolog-alert__body {
    flex: 1;
    min-width: 0;
}

.shopify-rehomolog-alert__badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--warning-bg);
    border: 1px solid var(--warning-border);
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
    color: var(--warning-fg);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 10px;
}

.shopify-rehomolog-alert__badge-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--warning-solid);
    animation: pulseDot 1.8s ease-in-out infinite;
    flex-shrink: 0;
}

@keyframes pulseDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.5; transform: scale(0.75); }
}

.shopify-rehomolog-alert__title {
    font-size: 14px;
    font-weight: 700;
    color: var(--warning-fg);
    margin: 0 0 6px 0;
    line-height: 1.4;
}

.shopify-rehomolog-alert__desc {
    font-size: 13px;
    color: var(--warning-fg);
    margin: 0 0 8px 0;
    line-height: 1.6;
}

.shopify-rehomolog-alert__desc strong {
    color: var(--warning-fg);
}

.shopify-rehomolog-alert__sub {
    font-size: 12px;
    color: var(--warning-fg);
    margin: 0;
    padding-top: 8px;
    border-top: 1px dashed var(--warning-border);
    line-height: 1.5;
}
</style>